/**
 * Acquisition Source Attribution Utility
 *
 * Captures which pseudo-tenant (e.g., coupling-wnc) acquired a user.
 * Stored in localStorage via ?source= URL parameter on login/signup pages.
 * First-touch model with 30-day expiry - once set, does not overwrite.
 */

const STORAGE_KEY = 'coupling_acquisition_source'
const STALENESS_MS = 30 * 24 * 60 * 60 * 1000 // 30 days
const SOURCE_PATTERN = /^[a-z0-9][a-z0-9._-]*$/

interface StoredSource {
  source: string
  timestamp: number
}

/**
 * Store acquisition source in localStorage (first-touch model).
 * Returns true if stored, false if already set or invalid.
 */
export function setAcquisitionSource(source: string): boolean {
  try {
    const trimmed = source?.trim().toLowerCase()
    if (!trimmed || trimmed.length > 100 || !SOURCE_PATTERN.test(trimmed))
      return false

    // First-touch: don't overwrite existing valid source
    const existing = getAcquisitionSource()
    if (existing) return false

    const data: StoredSource = {
      source: trimmed,
      timestamp: Date.now(),
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    return true
  } catch {
    return false
  }
}

/**
 * Read acquisition source from localStorage if present and not stale.
 * Removes stale entries as a side-effect.
 */
export function getAcquisitionSource(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const parsed = JSON.parse(raw)
    if (
      typeof parsed?.source !== 'string' ||
      typeof parsed?.timestamp !== 'number'
    ) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (Date.now() - parsed.timestamp > STALENESS_MS) {
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    return parsed.source
  } catch {
    return null
  }
}

/**
 * Clear the acquisition source from localStorage after successful auth.
 */
export function clearAcquisitionSource(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage errors
  }
}
